/**
 * Admin Audit Service — supervision para SUPER_ADMIN.
 */

import { get, tokenStorage } from '@/lib/api'
import { API_ROUTES } from '@/config/constants'

export interface AuditLogEntry {
  id: string
  userId: string | null
  userEmail: string | null
  userRole: string | null
  action: string
  resourceType: string
  resourceId: string | null
  resourceName: string | null
  details: Record<string, unknown> | null
  oldValues: Record<string, unknown> | null
  newValues: Record<string, unknown> | null
  ipAddress: string | null
  userAgent: string | null
  requestMethod: string | null
  requestPath: string | null
  requestId: string | null
  status: 'SUCCESS' | 'FAILURE' | 'DENIED'
  errorMessage: string | null
  createdAt: string
}

export interface AuditSearchFilters {
  userId?: string
  action?: string
  resourceType?: string
  resourceId?: string
  status?: 'SUCCESS' | 'FAILURE' | 'DENIED'
  ipAddress?: string
  dateFrom?: string
  dateTo?: string
  limit?: number
  offset?: number
}

export interface AuditSearchResult {
  logs: AuditLogEntry[]
  total: number
  limit: number
  offset: number
}

export interface SecurityStats {
  eventsToday: number
  events24h: number
  events7days: number
  failedLogins24h: number
  accessDenied24h: number
  successRate: number
  topActions: Array<{ action: string; count: number }>
  topUsers: Array<{ userEmail: string | null; count: number }>
  eventsByStatus: { SUCCESS: number; FAILURE: number; DENIED: number }
  eventsByHour: Array<{ hour: string; count: number }>
}

const buildQuery = (filters: AuditSearchFilters): string => {
  const params = new URLSearchParams()
  if (filters.userId) params.set('userId', filters.userId)
  if (filters.action) params.set('action', filters.action)
  if (filters.resourceType) params.set('resourceType', filters.resourceType)
  if (filters.resourceId) params.set('resourceId', filters.resourceId)
  if (filters.status) params.set('status', filters.status)
  if (filters.ipAddress) params.set('ipAddress', filters.ipAddress)
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom)
  if (filters.dateTo) params.set('dateTo', filters.dateTo)
  if (filters.limit !== undefined) params.set('limit', String(filters.limit))
  if (filters.offset !== undefined) params.set('offset', String(filters.offset))
  return params.toString()
}

export const searchAuditLogs = (filters: AuditSearchFilters): Promise<AuditSearchResult> => {
  const qs = buildQuery(filters)
  const url = qs ? `${API_ROUTES.ADMIN_AUDIT.SEARCH}?${qs}` : API_ROUTES.ADMIN_AUDIT.SEARCH
  return get(url)
}

export const getSecurityStats = (): Promise<SecurityStats> =>
  get(API_ROUTES.ADMIN_AUDIT.SECURITY_STATS)

/**
 * Exporta el CSV. No usa el wrapper get() porque es un descargable
 * (binary/text con headers de attachment) — usamos fetch directo.
 */
export const exportAuditCsv = async (filters: AuditSearchFilters): Promise<Blob> => {
  const qs = buildQuery(filters)
  const url = qs
    ? `/api/v1${API_ROUTES.ADMIN_AUDIT.EXPORT}?${qs}`
    : `/api/v1${API_ROUTES.ADMIN_AUDIT.EXPORT}`

  const token = tokenStorage.getAccessToken()
  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  })
  if (!response.ok) {
    throw new Error(`Export fallido: ${response.status}`)
  }
  return response.blob()
}
